import { useState } from "react"
import { ExpenseModel, updateExpense, deleteExpense } from "../reduxToolkit/expensesSlice"
import { useAppDispatch } from "../hooks/reduxToolkit"
import { TriageExpenseRow } from "./triageExpenseRow"
import { IncidentSelect } from "./formComponents/incidentSelect"
import { IncidentModel } from "../reduxToolkit/incidentsSlice"
import { present } from "../../lib/util/present"
import { useConfirm } from "../providers/confirmDialog"

export interface TriageExpenseListProps {
  expenses: ExpenseModel[]
  highlightExpenseId?: string | null
  /** Ids of incidents that are closed, used to derive each row's state. */
  closedIncidentIds: Set<string>
}

type BulkAction = 'assign' | null

export function TriageExpenseList({ expenses, highlightExpenseId, closedIncidentIds }: TriageExpenseListProps) {
  const dispatch = useAppDispatch()
  const [confirm] = useConfirm()
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activeBulkAction, setActiveBulkAction] = useState<BulkAction>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function exitSelectMode() {
    setIsSelectMode(false)
    setSelectedIds(new Set())
    setActiveBulkAction(null)
    setConfirmingDelete(false)
  }

  function toggleSelectMode() {
    if (isSelectMode) {
      exitSelectMode()
    } else {
      setIsSelectMode(true)
    }
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  function selectAll() {
    setSelectedIds(new Set(expenses.map((e) => e.id)))
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  function handleAssignToIncident(incident: IncidentModel | null) {
    if (!incident) return
    const now = new Date().toISOString()
    selectedIds.forEach((id) => {
      dispatch(updateExpense({ id, incident_id: incident.id, updated_at: now }))
    })
    exitSelectMode()
  }

  function handleDeleteExpense(expense: ExpenseModel) {
    confirm({
      message: 'Are you sure you want to delete this expense?',
    }).then((confirmed) => {
      if (confirmed) {
        const now = new Date().toISOString()
        dispatch(deleteExpense({ id: expense.id, updated_at: now, deleted_at: now }))
      }
    })
  }

  function handleDeleteSelected() {
    const now = new Date().toISOString()
    selectedIds.forEach((id) => {
      dispatch(deleteExpense({ id, updated_at: now, deleted_at: now }))
    })
    exitSelectMode()
  }

  const allSelected = expenses.length > 0 && expenses.every((e) => selectedIds.has(e.id))
  const someSelected = selectedIds.size > 0

  return (
    <div className="triage-expense-list">
      {/* Toolbar */}
      <div className="d-flex align-items-center mb-2">
        {isSelectMode ? (
          <>
            <input
              type="checkbox"
              className="form-check-input me-2"
              checked={allSelected}
              onChange={allSelected ? clearSelection : selectAll}
              title="Select all"
              aria-label="Select all"
            />
            <span className="small text-muted">
              {selectedIds.size} selected
            </span>
            <button className="btn btn-sm btn-link ms-auto" onClick={exitSelectMode}>
              Cancel
            </button>
          </>
        ) : (
          <button className="btn btn-sm btn-link ms-auto" onClick={toggleSelectMode}>
            Select
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="d-flex align-items-center gap-2 mb-2 p-2 bg-light border rounded flex-wrap">
          <span className="small fw-bold">
            {selectedIds.size} {selectedIds.size === 1 ? 'expense' : 'expenses'} selected
          </span>

          {activeBulkAction === 'assign' ? (
            <>
              <div style={{ minWidth: '200px' }}>
                <IncidentSelect
                  denyNewIncidents
                  onSelect={handleAssignToIncident}
                  placeholder="Choose incident…"
                />
              </div>
              <button
                className="btn btn-sm btn-link"
                onClick={() => setActiveBulkAction(null)}
              >
                Cancel
              </button>
            </>
          ) : confirmingDelete ? (
            <>
              <span className="small text-danger">
                Delete {selectedIds.size} {selectedIds.size === 1 ? 'expense' : 'expenses'}?
              </span>
              <button className="btn btn-sm btn-danger" onClick={handleDeleteSelected}>
                Confirm
              </button>
              <button
                className="btn btn-sm btn-link"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setActiveBulkAction('assign')}
              >
                Assign to incident
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Expense rows */}
      {expenses.slice().sort(byDateNullsFirst).map((expense) => (
        <TriageExpenseRow
          key={expense.id}
          expense={expense}
          closedIncidentIds={closedIncidentIds}
          isSelectMode={isSelectMode}
          isSelected={selectedIds.has(expense.id)}
          isHighlighted={expense.id === highlightExpenseId}
          onToggleSelected={toggleSelected}
          onRequestDelete={handleDeleteExpense}
        />
      ))}
    </div>
  )
}

function byDateNullsFirst(a: { date?: string | null }, b: { date?: string | null }) {
  if (!present(a.date)) return -1
  if (!present(b.date)) return 1
  return Date.parse(b.date) - Date.parse(a.date)
}
